import { useState, useEffect, useCallback } from 'react';
import { createTemps, getConsultants, getProjets, getClients, getPrestataires, getTemps, canCreateTemps, recalculerTMPTrimestre } from '../services/airtable';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { MOIS } from '../utils/constants';

/**
 * useTempsForm - Hook pour gérer le formulaire de saisie des temps
 *
 * Extrait de TempsForm.js pour une meilleure maintenabilité.
 * Gère l'état du formulaire, le chargement des données et la soumission.
 *
 * @param {Function} onSuccess - Callback après création réussie
 */
export function useTempsForm(onSuccess) {
  const { user, isSuperAdmin, isVisiteur } = useAuth();
  const { showToast } = useToast();

  // Calculer le mois précédent
  const getPreviousMonth = useCallback(() => {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1);
    return {
      year: previousMonth.getFullYear(),
      month: previousMonth.getMonth() + 1
    };
  }, []);

  // État du formulaire
  const [formData, setFormData] = useState(() => {
    const prevMonth = getPreviousMonth();
    return {
      'Année': prevMonth.year,
      'Mois': prevMonth.month,
      'Consultant': user?.consultant_id && !isSuperAdmin() ? [user.consultant_id] : [],
      'Prestataire': [],
      'Client': [],
      'Projet': [],
      'Nombre de jours': '',
      'Taux journalier (€)': '',
      'Notes': '',
      'tmpexcl': false
    };
  });

  // États des listes
  const [consultants, setConsultants] = useState([]);
  const [prestataires, setPrestataires] = useState([]);
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientsFiltres, setClientsFiltres] = useState([]);
  const [projetsFiltres, setProjetsFiltres] = useState([]);

  // États UI
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // État pour le blocage de facturation
  const [factureBloquee, setFactureBloquee] = useState(null);
  const [checkingFacture, setCheckingFacture] = useState(false);

  // Années disponibles
  const currentYear = new Date().getFullYear();
  const annees = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4, currentYear - 5];

  // Resynchronise le champ Consultant après une reconnexion (l'objet user change
  // de référence lors du login, ce qui peut vider la sélection pour les non-admins).
  useEffect(() => {
    if (user?.role !== ROLES.SUPER_ADMIN && user?.consultant_id) {
      setFormData(prev => {
        if (prev['Consultant']?.[0] === user.consultant_id) return prev;
        return { ...prev, 'Consultant': [user.consultant_id] };
      });
    }
  }, [user]);

  // === CHARGEMENT DES DONNÉES ===

  useEffect(() => {
    const loadData = async () => {
      try {
        const [consultantsData, projetsData, clientsData, prestatairesData] = await Promise.all([
          getConsultants(),
          getProjets(),
          getClients(),
          getPrestataires()
        ]);

        // Filtrer uniquement les consultants actifs
        const consultantsActifs = consultantsData.filter(c => c.fields.Statut === 'Actif');
        setConsultants(consultantsActifs);

        setProjets(projetsData);
        setProjetsFiltres([]);
        setClientsFiltres([]);

        // Mapper et filtrer les prestataires actifs
        const prestatairesList = prestatairesData
          .filter(p => p.fields.Statut === 'Actif')
          .map(p => ({
            id: p.id,
            nom: p.fields.Nom || p.fields['Nom du prestataire'] || p.fields.nom || 'Prestataire inconnu'
          }));
        setPrestataires(prestatairesList);

        // Mapper les clients depuis la table Clients finaux
        const clientsList = clientsData
          .filter(c => c.fields.Statut === 'Actif')
          .map(c => ({
            id: c.id,
            nom: c.fields.Nom || c.fields['Nom du client'] || c.fields.nom || 'Client inconnu',
            prestataireId: c.fields['Prestataire porteur']?.[0] || null
          }));
        setClients(clientsList);
      } catch (err) {
        console.error('Erreur détaillée:', err);
        showToast('Erreur lors du chargement des données', 'error');
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === VÉRIFICATION STATUT FACTURE ===

  const checkFactureStatus = useCallback(async () => {
    const annee = formData['Année'];
    const moisValue = formData['Mois'];
    const prestataireId = formData['Prestataire']?.[0];

    if (!annee || !moisValue || !prestataireId) {
      setFactureBloquee(null);
      return;
    }

    setCheckingFacture(true);
    try {
      const { canCreate, reason } = await canCreateTemps(annee, moisValue, prestataireId);
      setFactureBloquee(canCreate ? null : reason);
    } catch (err) {
      console.error('Erreur vérification facture:', err);
      setFactureBloquee(null);
    } finally {
      setCheckingFacture(false);
    }
  }, [formData]);

  useEffect(() => {
    checkFactureStatus();
  }, [checkFactureStatus]);

  // === FILTRES ===

  const filterClientsByPrestataire = useCallback((prestataireId) => {
    if (!prestataireId) {
      setClientsFiltres([]);
      return;
    }
    const filtered = clients.filter(client => client.prestataireId === prestataireId);
    setClientsFiltres(filtered);
  }, [clients]);

  const filterProjetsByClient = useCallback((clientId) => {
    if (!clientId) {
      setProjetsFiltres([]);
      return;
    }
    const filtered = projets.filter(projet => {
      const projetClientIds = projet.fields['Clients finaux'];
      return projetClientIds && projetClientIds.includes(clientId);
    });
    setProjetsFiltres(filtered);
  }, [projets]);

  // === HANDLERS ===

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'Prestataire') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? [value] : [],
        'Client': [],
        'Projet': []
      }));
      filterClientsByPrestataire(value);
      setProjetsFiltres([]);
    } else if (name === 'Client') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? [value] : [],
        'Projet': []
      }));
      filterProjetsByClient(value);
    } else if (name === 'Consultant' || name === 'Projet') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? [value] : []
      }));
    } else if (name === 'Nombre de jours' || name === 'Taux journalier (€)' || name === 'Année' || name === 'Mois') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseFloat(value) : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, [filterClientsByPrestataire, filterProjetsByClient]);

  const resetForm = useCallback(() => {
    const prevMonth = getPreviousMonth();
    setFormData({
      'Année': prevMonth.year,
      'Mois': prevMonth.month,
      'Consultant': user?.consultant_id && !isSuperAdmin() ? [user.consultant_id] : [],
      'Prestataire': [],
      'Client': [],
      'Projet': [],
      'Nombre de jours': '',
      'Taux journalier (€)': '',
      'Notes': '',
      'tmpexcl': false
    });
    setClientsFiltres([]);
    setProjetsFiltres([]);
    setFactureBloquee(null);
  }, [getPreviousMonth, isSuperAdmin, user]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (factureBloquee) {
      showToast(factureBloquee, 'error');
      return;
    }

    const nbJours = parseFloat(formData['Nombre de jours']);
    if (isNaN(nbJours) || nbJours <= 0 || nbJours > 999) {
      showToast('Le nombre de jours doit être compris entre 0 et 999', 'error');
      return;
    }

    setLoading(true);

    try {
      // Vérifier si la combinaison existe déjà
      const allTemps = await getTemps();
      const duplicate = allTemps.find(record =>
        record.fields.Année === parseInt(formData['Année'], 10) &&
        record.fields.Mois === parseInt(formData['Mois'], 10) &&
        record.fields.Consultant?.[0] === formData['Consultant'][0] &&
        record.fields.Projet?.[0] === formData['Projet'][0]
      );

      if (duplicate) {
        const consultantNom = consultants.find(c => c.id === formData['Consultant'][0])?.fields.Nom || 'Consultant';
        const projetNom = projetsFiltres.find(p => p.id === formData['Projet'][0])?.fields.Nom ||
                          projetsFiltres.find(p => p.id === formData['Projet'][0])?.fields['Nom du projet'] || 'Projet';
        const moisNom = MOIS.find(m => m.value === parseInt(formData['Mois'], 10))?.label || formData['Mois'];

        setDuplicateInfo({
          consultant: consultantNom,
          projet: projetNom,
          mois: moisNom,
          annee: formData['Année']
        });
        setShowDuplicateModal(true);
        setLoading(false);
        return;
      }

      // Préparer les données
      const dataToSend = {
        'Année': parseInt(formData['Année'], 10),
        'Mois': parseInt(formData['Mois'], 10),
        'Nombre de jours': parseFloat(formData['Nombre de jours']),
        'Taux journalier (€)': parseFloat(formData['Taux journalier (€)'])
      };

      if (formData['Consultant']?.[0]) dataToSend['Consultant'] = formData['Consultant'];
      if (formData['Projet']?.[0]) dataToSend['Projet'] = formData['Projet'];
      if (formData['Notes']?.trim()) dataToSend['Notes'] = formData['Notes'];
      if (formData['tmpexcl']) dataToSend['tmpexcl'] = true;

      const response = await createTemps(dataToSend, formData['Client']?.[0]);

      // Recalculer le TMP du trimestre
      const moisValue = parseInt(formData['Mois'], 10);
      const anneeValue = parseInt(formData['Année'], 10);
      const trimestre = moisValue <= 3 ? 'Q1' : moisValue <= 6 ? 'Q2' : moisValue <= 9 ? 'Q3' : 'Q4';

      try {
        await recalculerTMPTrimestre(anneeValue, trimestre);
      } catch (tmpError) {
        console.error('Erreur lors du recalcul du TMP:', tmpError);
      }

      // Réinitialiser le formulaire
      resetForm();
      setIsExpanded(false);

      // Message de succès
      const factureExterneCreated = response?.factureExterne?.created === true;
      const factureInterneCreated = response?.factureInterne?.created === true;

      let message = 'Temps enregistré avec succès.';
      if (factureExterneCreated && factureInterneCreated) {
        message += ' Factures externe et interne créées.';
      } else if (factureExterneCreated) {
        message += ' Facture externe créée.';
      } else if (factureInterneCreated) {
        message += ' Facture interne créée.';
      } else {
        message += ' Aucune nouvelle facture créée.';
      }

      showToast(message, 'success');

      if (onSuccess) onSuccess();
    } catch (err) {
      showToast('Erreur lors de la création : ' + (err.response?.data?.error?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, factureBloquee, consultants, projetsFiltres, showToast, onSuccess, resetForm]);

  // Vérifier si le formulaire peut être soumis
  const canSubmit = !loading && !factureBloquee && !checkingFacture && !isVisiteur();

  return {
    // État
    formData,
    consultants,
    prestataires,
    clientsFiltres,
    projetsFiltres,
    isExpanded,
    setIsExpanded,
    loading,
    showDuplicateModal,
    setShowDuplicateModal,
    duplicateInfo,
    factureBloquee,
    checkingFacture,
    annees,
    canSubmit,

    // Auth helpers
    user,
    isSuperAdmin,
    isVisiteur,

    // Handlers
    handleChange,
    handleSubmit
  };
}
